// ============================================
// ゲームエリア内HUD（コーナーフレーム・画面レティクル・ターゲットレティクル・情報テキスト）
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import { getGameAreaX, getGameAreaCenterX, easeOutCubic } from '../../utils/helpers';
import { getEnemies, getStageStartTimer, getDyingFadeAlpha } from '../../store/index';
import { HUD_COLORS } from '../../config/hud-colors';
import type { Enemy, BossEnemy } from '../../types/game-types';

// コーナーフレーム設定
const FRAME_SIZE = 24;
const FRAME_THICKNESS = 2;
const FRAME_MARGIN = 8;

// レティクルアニメーション設定（ステージ開始時のターゲティング演出）
const RETICLE_ANIM = {
    startProgress: 0.22,   // パネルスライド完了後に開始
    duration: 0.20,        // progress 0.22〜0.42 の区間（約36フレーム）
    startScale: 4.0,       // 初期サイズ倍率（最終サイズの4倍から収束）
};

/**
 * drawCornerFrames() - ゲームエリア四隅のL字ブラケット（グロー効果付き）
 * ステージ開始時はレティクルと同じターゲティングアニメーション付き
 */
function drawCornerFrames(): void {
    const animT = getReticleAnimProgress();
    if (animT <= 0) {
        return;  // アニメーション開始前は非表示
    }

    const easedT = easeOutCubic(animT);
    const frameSize = FRAME_SIZE * (1 + (RETICLE_ANIM.startScale - 1) * (1 - easedT));

    const p = getP5();
    const gx = getGameAreaX();
    const gw = CONFIG.width;
    const gh = CONFIG.height;
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const c = HUD_COLORS.cyan;

    ctx.save();
    ctx.strokeStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${0.6 * easedT})`;
    ctx.lineWidth = FRAME_THICKNESS;
    ctx.shadowColor = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${0.7 * easedT})`;
    ctx.shadowBlur = 12;

    const tlX = gx + FRAME_MARGIN;
    const tlY = FRAME_MARGIN;
    const trX = gx + gw - FRAME_MARGIN;
    const blY = gh - FRAME_MARGIN;

    // 左上
    ctx.beginPath();
    ctx.moveTo(tlX + frameSize, tlY);
    ctx.lineTo(tlX, tlY);
    ctx.lineTo(tlX, tlY + frameSize);
    ctx.stroke();

    // 右上
    ctx.beginPath();
    ctx.moveTo(trX - frameSize, tlY);
    ctx.lineTo(trX, tlY);
    ctx.lineTo(trX, tlY + frameSize);
    ctx.stroke();

    // 左下
    ctx.beginPath();
    ctx.moveTo(tlX, blY - frameSize);
    ctx.lineTo(tlX, blY);
    ctx.lineTo(tlX + frameSize, blY);
    ctx.stroke();

    // 右下
    ctx.beginPath();
    ctx.moveTo(trX - frameSize, blY);
    ctx.lineTo(trX, blY);
    ctx.lineTo(trX, blY - frameSize);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.restore();
}

/**
 * 十字マークを1つ描画（Canvas 2D API）
 */
function drawCross(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    halfSize: number,
    alpha: number,
    glowAlpha: number,
    blur: number
): void {
    const c = HUD_COLORS.cyan;
    ctx.strokeStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})`;
    ctx.shadowColor = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${glowAlpha})`;
    ctx.shadowBlur = blur;

    // 水平線
    ctx.beginPath();
    ctx.moveTo(x - halfSize, y);
    ctx.lineTo(x + halfSize, y);
    ctx.stroke();

    // 垂直線
    ctx.beginPath();
    ctx.moveTo(x, y - halfSize);
    ctx.lineTo(x, y + halfSize);
    ctx.stroke();
}

/**
 * stageStartTimerからレティクルアニメーション進捗(0〜1)を計算
 * 0: アニメーション開始前、1: 完了（通常表示）
 */
function getReticleAnimProgress(): number {
    const timer = getStageStartTimer();
    if (timer <= 0) {
        return 1;
    }

    const stageProgress = 1 - timer / CONFIG.stageStart.duration;
    if (stageProgress < RETICLE_ANIM.startProgress) {
        return 0;
    }

    const t = (stageProgress - RETICLE_ANIM.startProgress) / RETICLE_ANIM.duration;
    return Math.min(1, t);
}

/**
 * drawScreenReticle() - 画面レティクル（上下＋中央の3点十字）
 * ステージ開始時は広い位置から収束するターゲティングアニメーション付き
 */
function drawScreenReticle(): void {
    const animT = getReticleAnimProgress();
    if (animT <= 0) {
        return;  // アニメーション開始前は非表示
    }

    const easedT = easeOutCubic(animT);
    const scaleFactor = 1 + (RETICLE_ANIM.startScale - 1) * (1 - easedT);

    const p = getP5();
    const gh = CONFIG.height;
    const cx = getGameAreaCenterX();
    const cy = gh / 2;
    const ctx = p.drawingContext as CanvasRenderingContext2D;

    ctx.save();
    ctx.lineWidth = 1;

    // 中央
    drawCross(ctx, cx, cy, 20 * scaleFactor, 0.2 * easedT, 0.3 * easedT, 6);

    // 上下
    const edgeMargin = FRAME_MARGIN + FRAME_SIZE + 8;
    drawCross(ctx, cx, edgeMargin, 8 * scaleFactor, 0.2 * easedT, 0.3 * easedT, 6);       // 上
    drawCross(ctx, cx, gh - edgeMargin, 8 * scaleFactor, 0.2 * easedT, 0.3 * easedT, 6);  // 下

    ctx.shadowBlur = 0;
    ctx.restore();
}

/**
 * drawGameAreaInfo() - ゲームエリア四隅の情報テキスト
 */
function drawGameAreaInfo(): void {
    const p = getP5();
    const gx = getGameAreaX();
    const gw = CONFIG.width;
    const gh = CONFIG.height;
    const fps = Math.round(p.frameRate());

    p.textFont('Share Tech Mono');
    p.textSize(8);
    p.fill(HUD_COLORS.cyan[0], HUD_COLORS.cyan[1], HUD_COLORS.cyan[2], 102);
    p.noStroke();

    // 左上: システムステータス
    p.textAlign(p.LEFT, p.TOP);
    p.text('SYS:ONLINE', gx + 36, 12);

    // 右上: FPS
    p.textAlign(p.RIGHT, p.TOP);
    p.text(`FPS:${fps}`, gx + gw - 36, 12);

    // 左下: 高度（フレーバーテキスト）
    p.textAlign(p.LEFT, p.BOTTOM);
    p.text('ALT:420KM', gx + 36, gh - 12);

    // 右下: 弾薬（無限）
    p.textAlign(p.RIGHT, p.BOTTOM);
    p.text('AMMO:\u221E', gx + gw - 36, gh - 12);
}

// レティクル離脱アニメーション設定
const RETICLE_DETACH = {
    maxSpread: 35,     // 最大離脱距離（px）
};

/**
 * drawTargetReticle() - 敵ターゲットレティクル（控えめなコーナーマーク）
 * 被弾フェード時はコーナーが四方に離脱するアニメーション付き
 */
function drawTargetReticle(enemy: Enemy, fadeAlpha: number): void {
    const p = getP5();
    const margin = 8;
    const size = enemy.size + margin * 2;
    const halfSize = size / 2;
    const cornerLen = Math.max(6, size * 0.22);

    // 離脱アニメーション進捗（0: 通常、1: 完全離脱）
    const detachT = 1 - fadeAlpha;
    const eased = detachT * detachT; // easeIn: 加速しながら離脱
    const spread = eased * RETICLE_DETACH.maxSpread;
    const alpha = 77 * fadeAlpha;

    if (alpha <= 0) {
        return;
    }

    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const c = HUD_COLORS.cyan;

    ctx.save();
    ctx.translate(enemy.x, enemy.y);
    ctx.strokeStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha / 255})`;
    ctx.lineWidth = 1;

    // 各コーナーを個別に描画（離脱時は各自の対角方向へ移動）

    // 左上コーナー
    ctx.beginPath();
    ctx.moveTo(-halfSize + cornerLen - spread, -halfSize - spread);
    ctx.lineTo(-halfSize - spread, -halfSize - spread);
    ctx.lineTo(-halfSize - spread, -halfSize + cornerLen - spread);
    ctx.stroke();

    // 右上コーナー
    ctx.beginPath();
    ctx.moveTo(halfSize - cornerLen + spread, -halfSize - spread);
    ctx.lineTo(halfSize + spread, -halfSize - spread);
    ctx.lineTo(halfSize + spread, -halfSize + cornerLen - spread);
    ctx.stroke();

    // 左下コーナー
    ctx.beginPath();
    ctx.moveTo(-halfSize + cornerLen - spread, halfSize + spread);
    ctx.lineTo(-halfSize - spread, halfSize + spread);
    ctx.lineTo(-halfSize - spread, halfSize - cornerLen + spread);
    ctx.stroke();

    // 右下コーナー
    ctx.beginPath();
    ctx.moveTo(halfSize - cornerLen + spread, halfSize + spread);
    ctx.lineTo(halfSize + spread, halfSize + spread);
    ctx.lineTo(halfSize + spread, halfSize - cornerLen + spread);
    ctx.stroke();

    ctx.restore();
}

/**
 * drawBossReticle() - ボス用大型レティクル
 */
function drawBossReticle(boss: Enemy): void {
    const p = getP5();
    const width = 140;
    const height = 110;

    p.push();
    p.translate(boss.x, boss.y);

    const pulse = 1 + Math.sin(p.frameCount * 0.15) * 0.03;
    p.scale(pulse);

    // 大型枠線
    p.noFill();
    p.stroke(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], 128);
    p.strokeWeight(2);
    p.rect(-width / 2, -height / 2, width, height, 4);

    // DANGER ラベル（点滅）
    const blinkAlpha = p.frameCount % 20 < 10 ? 255 : 128;
    p.noStroke();
    p.fill(HUD_COLORS.red[0], HUD_COLORS.red[1], HUD_COLORS.red[2], blinkAlpha);
    p.textFont('Share Tech Mono');
    p.textSize(9);
    p.textAlign(p.CENTER, p.BOTTOM);
    p.text('\u26A0 DANGER', 0, -height / 2 - 10);

    p.pop();
}

/**
 * drawGameAreaHUD() - ゲームエリアHUDの統合オーケストレーター
 */
export function drawGameAreaHUD(): void {
    const p = getP5();

    drawCornerFrames();
    drawScreenReticle();
    drawGameAreaInfo();

    // 敵レティクル
    const allEnemies = getEnemies();
    const boss = allEnemies.find(e => e.type === 'boss') as BossEnemy | undefined;
    const fadeAlpha = getDyingFadeAlpha();

    if (boss) {
        // ボス戦モード: ボスレティクルのみ
        drawBossReticle(boss);
    } else if (fadeAlpha > 0) {
        // 通常モード: すべての敵にターゲットレティクル（フェード中は離脱アニメーション）
        for (const enemy of allEnemies) {
            drawTargetReticle(enemy, fadeAlpha);
        }
    }

    // フォントを戻す
    p.textFont('Orbitron');
}
